import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertCircle, Sparkles } from '../components/Icons';
import { API } from '../contexts/AuthContext';
import { formatDate } from '../lib/date';

interface CreditTransaction {
  id: string;
  delta: string;
  reason: string;
  balanceAfter: string;
  createdAt: string;
}

interface CreditsData {
  balance: string;
  subscriptionBalance: string;
  permanentBalance: string;
  transactions: CreditTransaction[];
}

const REASON_META: Record<string, { label: string; icon: string }> = {
  REGISTRATION_BONUS: { label: 'Welcome Bonus',     icon: '🎁' },
  GENERATION:         { label: 'AI Generation',     icon: '✨' },
  REFUND:             { label: 'Refund',             icon: '↩️' },
  TOPUP:              { label: 'Credit Top-up',      icon: '💳' },
  SUBSCRIPTION:       { label: 'Subscription Grant', icon: '📦' },
  ADMIN_ADJUSTMENT:   { label: 'Adjustment',         icon: '⚙️' },
  EXPIRY:             { label: 'Credits Expired',    icon: '⏳' },
};

function reasonMeta(reason: string) {
  return REASON_META[reason] ?? { label: reason, icon: '•' };
}

function groupByDate(txs: CreditTransaction[]): Array<{ label: string; items: CreditTransaction[] }> {
  const map = new Map<string, CreditTransaction[]>();
  const today     = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);

  for (const tx of txs) {
    const day = tx.createdAt.slice(0, 10);
    if (!map.has(day)) map.set(day, []);
    map.get(day)!.push(tx);
  }

  return Array.from(map.entries()).map(([day, items]) => {
    let label: string;
    if (day === fmt(today))     label = 'Today';
    else if (day === fmt(yesterday)) label = 'Yesterday';
    else {
      const d = new Date(day + 'T00:00:00');
      label = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    }
    return { label, items };
  });
}

function txTime(createdAt: string): string {
  const d = new Date(createdAt);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function Transactions() {
  const navigate = useNavigate();
  const [data, setData]       = useState<CreditsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError]     = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get<CreditsData>(`${API}/credits`);
        setData(res.data);
      } catch {
        setError('Failed to load transaction history. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = data ? groupByDate(data.transactions) : [];

  return (
    <div className="page-root">
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <span className="heading-brand" style={{ fontSize: '0.875rem', letterSpacing: '0.06em' }}>
            TRANSACTION HISTORY
          </span>
          <div style={{ width: 36 }} />
        </div>
      </header>

      <div className="page-scroll">
        <div className="page-container" style={{ paddingTop: 24, paddingBottom: 48 }}>

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <div className="spinner" />
            </div>
          )}

          {error && (
            <div className="alert alert-error" role="alert">
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Balance summary */}
              <div className="tx-balance-card">
                <div className="tx-balance-label">
                  <Sparkles size={13} color="var(--accent)" />
                  Current Balance
                </div>
                <div className="tx-balance-amount">{data.balance} credits</div>
              </div>

              {data.transactions.length === 0 ? (
                <div className="credits-empty" style={{ marginTop: 32 }}>No transactions yet.</div>
              ) : (
                <>
                  {groups.map(group => (
                    <div key={group.label} className="tx-group">
                      <div className="tx-group-label">{group.label}</div>
                      <div className="credits-tx-list">
                        {group.items.map(tx => {
                          const positive = !tx.delta.startsWith('-');
                          const meta     = reasonMeta(tx.reason);
                          const absVal   = tx.delta.replace(/^-/, '');
                          return (
                            <div key={tx.id} className="tx-row">
                              <div className="tx-icon">{meta.icon}</div>
                              <div className="tx-info">
                                <div className="tx-reason">{meta.label}</div>
                                <div className="tx-time">{txTime(tx.createdAt)}</div>
                              </div>
                              <div className="tx-right">
                                <span className={`tx-delta ${positive ? 'positive' : 'negative'}`}>
                                  {positive ? `+${absVal}` : `−${absVal}`} cr
                                </span>
                                <span className="tx-balance-after">{tx.balanceAfter} left</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {data.transactions.length === 20 && (
                    <p style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: 16 }}>
                      Showing last 20 transactions
                    </p>
                  )}
                </>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default Transactions;
